import React from 'react';
import { Users, Sparkles, Bug, Wind, Wrench } from 'lucide-react';
import { Reveal } from '../../hooks/useReveal';

/**
 * TeamShowcase — cinematic full-width AI-rendered hero band that sits
 * just below the Hero section on the home page.
 *
 * Visual highlights:
 *  • One ultra-wide AI image of the EntoRid crew in action
 *  • Slow Ken-Burns zoom that breathes
 *  • Animated floating badges that "pin" to roles in the photo
 *  • Light spotlight sweep across the image
 *  • Trust stat strip below
 *
 * The component is responsive — the SAME image is used on every viewport,
 * the height just scales (aspect-ratio + max-height) for mobile / tablet /
 * desktop.
 */
const PINS = [
  // Approx coordinates over the AI image — tuned to keep them out of faces
  { key: 'fogging',   icon: Wind,   label: 'Mosquito Fogging',  x: '12%', y: '38%', accent: 'bg-sky-500'    },
  { key: 'spray',     icon: Bug,    label: 'Cockroach Spray',   x: '86%', y: '60%', accent: 'bg-rose-500'   },
  { key: 'team',      icon: Users,  label: 'Our Crew',           x: '50%', y: '24%', accent: 'bg-emerald-500'},
  { key: 'equipment', icon: Wrench, label: 'Pro Equipment',      x: '52%', y: '86%', accent: 'bg-amber-500'  },
];

// Pins are pure decoration over the AI image — using stable lucide icons.

export default function TeamShowcase() {
  return (
    <section
      className="relative w-full overflow-hidden bg-[#06121F]"
      data-testid="team-showcase-section"
      style={{
        backgroundImage:
          'linear-gradient(180deg, #06121F 0%, #0A1A2E 50%, #06121F 100%)',
      }}
    >
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Eyebrow + headline */}
        <Reveal>
          <div className="text-center mb-6 sm:mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 backdrop-blur-md border border-emerald-300/30 text-[11px] uppercase tracking-[0.25em] font-bold text-emerald-300 mb-3">
              <Sparkles size={13} /> Meet The Team
            </div>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white"
              style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", serif' }}
            >
              <span className="italic text-emerald-300">Real People.</span> Real Pest Control.
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-slate-300/80 max-w-xl mx-auto leading-relaxed">
              Trained technicians, dedicated office crew, and the same calibrated equipment
              every single day.
            </p>
          </div>
        </Reveal>

        {/* Image stage */}
        <Reveal delay={0.15}>
          <div
            className="relative w-full rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-slate-900"
            style={{
              aspectRatio: '21 / 9',
              maxHeight: '640px',
            }}
            data-testid="team-showcase-image-stage"
          >
            {/* Image */}
            <img
              src="/images/pest-control/team-showcase.png"
              alt="EntoRid pest control team in action — technicians fogging, spraying, and crew with equipment"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              style={{
                transformOrigin: 'center center',
                animation: 'teamKenBurns 18s ease-in-out infinite alternate',
              }}
            />

            {/* Vignette */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)',
              }}
              aria-hidden
            />

            {/* Spotlight sweep */}
            <div
              className="absolute inset-y-0 w-1/3 pointer-events-none mix-blend-screen"
              style={{
                background:
                  'linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.10) 45%, rgba(16,185,129,0.16) 50%, rgba(255,255,255,0.10) 55%, transparent 100%)',
                animation: 'teamSpotlight 7s ease-in-out infinite',
              }}
              aria-hidden
            />

            {/* Floating pins moved OUT of the photo (kept below) so they don't cover faces */}

            {/* Bottom caption strip */}
            <div className="absolute inset-x-0 bottom-0 p-3 sm:p-5 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
              <div className="flex flex-wrap items-center justify-between gap-2 text-white">
                <div className="text-[11px] sm:text-sm font-semibold tracking-wide">
                  <span className="text-emerald-300">EntoRid</span> · Operations Bay · Uttarakhand
                </div>
                <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-white/70">
                  Captured on duty
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Annotated chip row — sits BELOW the photo so it never covers any
            crew member's face. On mobile it wraps to a clean 2×2 grid. */}
        <Reveal delay={0.22}>
          <div
            className="mt-4 sm:mt-6 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3"
            data-testid="team-showcase-pin-row"
          >
            {PINS.map((p, i) => {
              const Icon = p.icon || Sparkles;
              return (
                <div
                  key={p.key}
                  data-testid={`team-pin-${p.key}`}
                  className="group flex items-center gap-2 px-3 py-2 sm:py-2.5 rounded-full bg-white/[0.04] backdrop-blur-md border border-white/10 hover:border-white/25 transition-colors"
                  style={{
                    animation: `teamPinFloat ${4 + i * 0.5}s ease-in-out ${i * 0.3}s infinite alternate`,
                  }}
                >
                  <span className={`relative inline-flex w-2.5 h-2.5 rounded-full ${p.accent} shrink-0 shadow-[0_0_14px_rgba(255,255,255,0.5)]`}>
                    <span className={`absolute inset-0 rounded-full ${p.accent} opacity-75 animate-ping`} />
                  </span>
                  <Icon size={13} strokeWidth={2.2} className="text-white/80 shrink-0" />
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-bold text-white whitespace-nowrap truncate">
                    {p.label}
                  </span>
                </div>
              );
            })}
          </div>
        </Reveal>

        {/* Mini stat strip */}
        <Reveal delay={0.3}>
          <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-3 sm:gap-6 max-w-3xl mx-auto">
            {[
              { k: '20+', v: 'Trained Technicians' },
              { k: '5★',  v: 'Calibrated Equipment' },
              { k: '24/7', v: 'Service Across UK' },
            ].map((s) => (
              <div
                key={s.v}
                className="rounded-xl bg-white/5 backdrop-blur-md border border-white/10 px-3 py-3 sm:py-4 text-center"
              >
                <div className="text-lg sm:text-2xl font-bold text-emerald-300 leading-none">{s.k}</div>
                <div className="mt-1 text-[10px] sm:text-xs uppercase tracking-[0.18em] text-slate-300/80">{s.v}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      <style>{`
        @keyframes teamKenBurns {
          0%   { transform: scale(1.02) translate(0, 0); }
          100% { transform: scale(1.10) translate(-1.2%, -0.6%); }
        }
        @keyframes teamSpotlight {
          0%   { transform: translateX(-120%); }
          100% { transform: translateX(220%); }
        }
        @keyframes teamPinFloat {
          0%   { transform: translateY(0); }
          100% { transform: translateY(-3px); }
        }
      `}</style>
    </section>
  );
}
